import { useMemo, useState } from "react";
import { jobsApi, ApiError } from "@/api";
import { Button, TextField } from "@/components/ui";
import {
  lookupPostcodeAddresses,
  postcodeLookupAvailable,
  type PostcodeAddress,
} from "@/lib/postcode";
import { FormDrawer } from "./FormDrawer";

/** Job Detail → "Get Detailed Address" drawer. Resolves a job's full structured
 *  address from a postcode via Ideal Postcodes, lets the user pick the right
 *  premise, previews exactly what will be written, then PATCHes the job
 *  (address_line_1..3, city, county, postcode, uprn, lat, lng) via
 *  {Jobs}/update_address. Backdrop click is disabled (closeOnOverlayClick=false)
 *  so an accidental click can't discard an in-progress selection. */
export function GetDetailedAddressDrawer({
  jobId,
  fullAddress,
  postcode,
  onClose,
  onSaved,
}: {
  jobId: number;
  fullAddress: string;
  postcode: string;
  onClose: () => void;
  /** Called after a successful save — the parent closes + re-fetches /detail. */
  onSaved: () => void;
}) {
  const [firstLine, setFirstLine] = useState((fullAddress.split(",")[0] ?? "").trim());
  const [pc, setPc] = useState(postcode ?? "");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<PostcodeAddress[] | null>(null);
  const [searchError, setSearchError] = useState<string | undefined>();
  const [selected, setSelected] = useState<PostcodeAddress | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>();

  // The item whose line_1 case-insensitively matches the typed first line — the
  // likely-correct premise, pre-highlighted to draw the eye (not auto-selected).
  const matchKey = firstLine.trim().toLowerCase();
  const matchIndex = useMemo(() => {
    if (!results || !matchKey) return -1;
    return results.findIndex((r) => r.line1.trim().toLowerCase() === matchKey);
  }, [results, matchKey]);

  async function search() {
    const q = pc.trim();
    if (!q || searching) return;
    setSearching(true);
    setSearchError(undefined);
    setResults(null);
    setSelected(null);
    try {
      const found = await lookupPostcodeAddresses(q);
      if (found.length === 0) {
        setSearchError("No addresses found for that postcode — check it and try again.");
        return;
      }
      setResults(found);
    } catch (e) {
      setSearchError(e instanceof Error ? e.message : "Postcode lookup failed.");
    } finally {
      setSearching(false);
    }
  }

  async function save() {
    if (!selected || saving) return;
    setSaving(true);
    setSaveError(undefined);
    try {
      await jobsApi.updateJobAddress({
        id: jobId,
        address_line_1: selected.line1,
        address_line_2: selected.line2,
        address_line_3: selected.line3,
        city: selected.postTown,
        county: selected.county,
        postcode: selected.postcode,
        uprn: selected.uprn,
        lat: selected.latitude,
        lng: selected.longitude,
      });
      onSaved();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : "Could not save the address.");
      setSaving(false);
    }
  }

  return (
    <FormDrawer
      title="Get Detailed Address"
      busy={saving}
      closeOnOverlayClick={false}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={save} loading={saving} disabled={!selected}>
            Save to job
          </Button>
        </>
      }
    >
      <section className="job-drawer__section">
        <div className="addr-current">
          <span className="addr-current__label">Current address</span>
          <span className="addr-current__value">{fullAddress || "—"}</span>
        </div>

        <TextField
          label="First line of address"
          value={firstLine}
          onChange={(e) => setFirstLine(e.target.value)}
          placeholder="e.g. 29 Spicer Close"
          hint="Used to highlight the matching address in the results below."
        />

        <div className="addr-search">
          <TextField
            label="Postcode"
            className="addr-search__pc"
            value={pc}
            onChange={(e) => setPc(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                search();
              }
            }}
            placeholder="e.g. KT12 2YA"
          />
          <Button
            variant="secondary"
            onClick={search}
            loading={searching}
            disabled={!pc.trim() || !postcodeLookupAvailable}
            className="addr-search__btn"
          >
            Search
          </Button>
        </div>

        {!postcodeLookupAvailable ? (
          <p className="job-drawer__muted">Address lookup isn’t configured (no Ideal Postcodes API key).</p>
        ) : null}

        {searchError ? (
          <p className="leads__error" role="alert" onClick={() => setSearchError(undefined)}>{searchError}</p>
        ) : null}
      </section>

      {results && results.length > 0 ? (
        <section className="job-drawer__section">
          <h3>Select an address ({results.length} found)</h3>
          <ul className="addr-results">
            {results.map((r, i) => {
              const isSelected = selected === r;
              const isMatch = i === matchIndex;
              const cls =
                "addr-result" +
                (isSelected ? " addr-result--selected" : "") +
                (isMatch && !isSelected ? " addr-result--match" : "");
              return (
                <li key={r.uprn || i}>
                  <button type="button" className={cls} onClick={() => setSelected(r)} aria-pressed={isSelected}>
                    <span className="addr-result__line">
                      {[r.line1, r.line2, r.postTown].filter(Boolean).join(", ")}
                    </span>
                    {isMatch && !isSelected ? <span className="addr-result__tag">Likely match</span> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {selected ? (
        <section className="job-drawer__section">
          <h3>Will be saved to the job</h3>
          <dl className="addr-preview">
            <PreviewRow label="Address line 1" value={selected.line1} />
            <PreviewRow label="Address line 2" value={selected.line2} />
            <PreviewRow label="Address line 3" value={selected.line3} />
            <PreviewRow label="City" value={selected.postTown} />
            <PreviewRow label="County" value={selected.county} />
            <PreviewRow label="Postcode" value={selected.postcode} />
            <PreviewRow label="UPRN" value={selected.uprn} />
          </dl>
          {saveError ? (
            <p className="leads__error" role="alert" onClick={() => setSaveError(undefined)}>{saveError}</p>
          ) : null}
        </section>
      ) : null}
    </FormDrawer>
  );
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="addr-preview__row">
      <dt>{label}</dt>
      <dd>{value ? value : <span className="job-drawer__muted">—</span>}</dd>
    </div>
  );
}
